import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  type CustomField,
  type CustomFieldValue,
  dedupeCustomFieldValuesByKeyLast,
  FETCH_CUSTOM_FIELDS,
  mergeOvAndCustodianCustomFields,
} from '@interfaces/customField';
import type { EnabledJurisdictions } from '@interfaces/organization';
import { SubStepTypes } from '@interfaces/workflow';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { formatPhoneNumber } from '@utils/common';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { Skeleton, useSnackbar } from 'hephaestus/web';
import { isEmpty } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnabledCustomFields } from '../utils';
import { PersonalInformationVisual } from './personalInformation.visual';

const FETCH_USER_PERSONAL_INFO = (
  derivedCustomFields?: string[],
  enableCustodianCustomFields?: boolean,
) => gql`
  query fetchUserPersonalInfo($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        firstName
        middleName
        lastName
        primaryEmail
        phone
        dateOfBirth
        language
        preferredMethodOfCommunication
        inProvinceSince
        gender
        physicalAddress { city jurisdiction streetName postal unitNumber houseNumber neighborhood country }
        mailingAddress { city jurisdiction streetName postal unitNumber houseNumber neighborhood country }
        alternateAddress { line1 line2 city country postal jurisdiction }
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
        ${enableCustodianCustomFields ? 'custodianCustomFields { key value }' : ''}
        organization {
          jurisdictions { all only }
          applicableLocalization { countries }
        }
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const UPDATE_CUSTODIAN_CUSTOM_FIELDS = gql`
  mutation UpdateCustodianCustomFields(
    $input: UpdateCustodianCustomFieldsInput!
  ) {
    updateCustodianCustomFields(input: $input) {
      custodianCustomFields {
        key
        value
      }
    }
  }
`;

export const PersonalInformation = ({
  options,
  userId,
  onNext,
  stepLoading,
  grid = false,
  updateMode = false,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
}) => {
  const { t } = useTranslation(['shared']);
  const { activeOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const [hasMailingAddress, setHasMailingAddress] = useState(false);
  const [hasAlternateAddress, setHasAlternateAddress] = useState(false);
  const canReadCustomFields = usePermission('read:custom_fields');
  const enabledCustomFields = getEnabledCustomFields(options);
  const { custodianConnection } = useContext(UserContext);
  const enableCustodianCustomFields =
    custodianConnection?.enableFetchCustodianCustomFields;
  const [userData, setUserData] = useState<any>({
    id: '',
    firstName: '',
    middleName: '',
    lastName: '',
    primaryEmail: '',
    phone: '',
    dateOfBirth: '',
    language: '',
    preferredMethodOfCommunication: '',
    inProvinceSince: '',
    gender: '',
    physicalAddress: {
      city: '',
      Jurisdiction: '',
      streetName: '',
      postal: '',
      unitNumber: '',
      houseNumber: '',
      neighborhood: '',
      country: '',
    },
    mailingAddress: undefined,
    alternateAddress: undefined,
    customFields: [],
  });
  const custodianFieldKeys = useRef<Set<string>>(new Set());
  const [applicableJurisdictions, setApplicableJurisdictions] =
    useState<EnabledJurisdictions>({ all: true });
  const [applicableCountries, setApplicableCountries] = useState<
    CountryCodes[]
  >([]);

  const customFieldsPermitted =
    canReadCustomFields && enabledCustomFields.length !== 0;

  // loading 1 - custom fields
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.PERSONAL_INFORMATION,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - user's personal info
  const { data, loading } = useQuery(
    FETCH_USER_PERSONAL_INFO(
      derivedCustomFieldsKeys,
      enableCustodianCustomFields,
    ),
    {
      variables: { userId },
      fetchPolicy: 'no-cache',
      skip: customFieldsLoading,
    },
  );

  useEffect(() => {
    if (userId && !workflowData.currentUserId) {
      setWorkflowData({
        ...workflowData,
        currentUserId: userId,
      });
    }
  }, [userId, workflowData]);

  const [
    updateCustodianCustomFieldsMutation,
    { loading: updateCustodianCustomFieldsLoading },
  ] = useMutation(UPDATE_CUSTODIAN_CUSTOM_FIELDS);

  const [updateUserMutation, { loading: updateUserLoading }] = useMutation(
    UPDATE_USER,
    {
      context: {
        headers: getWorkflowCompletionHeaders(workflowCompletion),
      },
      onCompleted: () => {
        if (updateMode) {
          enqueueSnackbar(t('profileDetailsUpdated'), { variant: 'success' });
        }
        onNext();
      },
      onError: (err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      },
    },
  );

  const updateUser = async () => {
    if (enableCustodianCustomFields) {
      await updateCustodianCustomFieldsMutation({
        variables: {
          input: {
            filter: {
              userId,
              custodianCustomFields: modifyUpdatingCustomFields(
                dedupeCustomFieldValuesByKeyLast(
                  userData?.customFields?.filter((f: CustomFieldValue) =>
                    custodianFieldKeys.current.has(f.key),
                  ),
                ),
                derivedCustomFields ?? [],
                derivedCustomFieldsKeys,
              ),
            },
          },
          skipErrorHandler: true,
        },
      });
    }

    await updateUserMutation({
      variables: {
        input: {
          // removing id from userData because graphql doesn't support id in update mutation
          ...Object.fromEntries(
            Object.entries(userData).filter(([key]) => !['id'].includes(key)),
          ),
          physicalAddress: isEmpty(userData.physicalAddress)
            ? undefined
            : userData.physicalAddress,
          mailingAddress: isEmpty(userData.mailingAddress)
            ? null
            : userData.mailingAddress,
          alternateAddress: isEmpty(userData.alternateAddress)
            ? null
            : userData.alternateAddress,
          customFields: enableCustodianCustomFields
            ? modifyUpdatingCustomFields(
                userData?.customFields?.filter(
                  (f: CustomFieldValue) =>
                    !custodianFieldKeys.current.has(f.key),
                ),
                derivedCustomFields ?? [],
                derivedCustomFieldsKeys,
              )
            : modifyUpdatingCustomFields(
                userData?.customFields,
                derivedCustomFields ?? [],
                derivedCustomFieldsKeys,
              ),
          userId,
        },
        skipErrorHandler: true,
      },
    });
  };

  useEffect(() => {
    if (data) {
      const ovCustomFields = [...(data.fetchUser?.user?.customFields || [])];
      const custodianCustomFields =
        data.fetchUser?.user?.custodianCustomFields || [];
      custodianFieldKeys.current = new Set<string>(
        custodianCustomFields.map((f: CustomFieldValue) => f.key),
      );
      const combinedCustomFields = mergeOvAndCustodianCustomFields(
        ovCustomFields,
        custodianCustomFields,
      );

      setUserData({
        id: data?.fetchUser?.user?.id || '',
        firstName: data.fetchUser.user.firstName || undefined,
        middleName: data.fetchUser.user.middleName || undefined,
        lastName: data.fetchUser.user.lastName || undefined,
        primaryEmail: data.fetchUser.user.primaryEmail || undefined,
        phone: formatPhoneNumber(data.fetchUser.user.phone) || undefined,
        dateOfBirth: data.fetchUser.user.dateOfBirth || undefined,
        language: data.fetchUser.user.language || undefined,
        preferredMethodOfCommunication:
          data.fetchUser.user.preferredMethodOfCommunication || undefined,
        inProvinceSince: data.fetchUser.user.inProvinceSince || undefined,
        gender: data.fetchUser.user.gender || undefined,
        physicalAddress: data.fetchUser.user.physicalAddress
          ? {
              city: data.fetchUser.user.physicalAddress?.city || undefined,
              jurisdiction:
                data.fetchUser.user.physicalAddress?.jurisdiction || undefined,
              streetName:
                data.fetchUser.user.physicalAddress?.streetName || undefined,
              postal: data.fetchUser.user.physicalAddress?.postal || undefined,
              unitNumber:
                data.fetchUser.user.physicalAddress?.unitNumber || undefined,
              houseNumber:
                data.fetchUser.user.physicalAddress?.houseNumber || undefined,
              neighborhood:
                data.fetchUser.user.physicalAddress?.neighborhood || undefined,
              country:
                data.fetchUser.user.physicalAddress?.country || undefined,
            }
          : {},
        mailingAddress: data.fetchUser.user.mailingAddress
          ? {
              city: data.fetchUser.user.mailingAddress?.city || undefined,
              jurisdiction:
                data.fetchUser.user.mailingAddress?.jurisdiction || undefined,
              streetName:
                data.fetchUser.user.mailingAddress?.streetName || undefined,
              postal: data.fetchUser.user.mailingAddress?.postal || undefined,
              unitNumber:
                data.fetchUser.user.mailingAddress?.unitNumber || undefined,
              houseNumber:
                data.fetchUser.user.mailingAddress?.houseNumber || undefined,
              neighborhood:
                data.fetchUser.user.mailingAddress?.neighborhood || undefined,
              country: data.fetchUser.user.mailingAddress?.country || undefined,
            }
          : {},
        alternateAddress: data.fetchUser.user.alternateAddress
          ? {
              line1: data.fetchUser.user.alternateAddress?.line1 || undefined,
              line2: data.fetchUser.user.alternateAddress?.line2 || undefined,
              city: data.fetchUser.user.alternateAddress?.city || undefined,
              country:
                data.fetchUser.user.alternateAddress?.country || undefined,
              postal: data.fetchUser.user.alternateAddress?.postal || undefined,
              jurisdiction:
                data.fetchUser.user.alternateAddress?.jurisdiction || undefined,
            }
          : {},
        customFields: combinedCustomFields,
      });
      setApplicableJurisdictions(
        data.fetchUser?.user?.organization?.jurisdictions,
      );
      setApplicableCountries(
        data.fetchUser?.user?.organization?.applicableLocalization?.countries,
      );
      setHasMailingAddress(
        Boolean(data.fetchUser.user.mailingAddress) &&
          !isEmpty(data.fetchUser.user.mailingAddress),
      );
      setHasAlternateAddress(
        Boolean(data.fetchUser.user.alternateAddress) &&
          !isEmpty(data.fetchUser.user.alternateAddress),
      );
    }
  }, [data]);

  return (
    <>
      {customFieldsLoading || loading || stepLoading ? (
        <>
          <Skeleton
            width='200px'
            height='33px'
            variant='text'
            data-testid='custom-field-skeleton'
          />
          <Skeleton
            width='100px'
            height='19px'
            variant='text'
            data-testid='custom-field-skeleton'
          />
        </>
      ) : (
        <PersonalInformationVisual
          options={options}
          userData={userData}
          updateUser={setUserData}
          continueFunc={updateUser}
          loading={
            customFieldsLoading ||
            loading ||
            updateUserLoading ||
            stepLoading ||
            updateCustodianCustomFieldsLoading
          }
          grid={grid}
          updateMode={updateMode}
          activeCustomFields={derivedCustomFieldsKeys}
          derivedCustomFields={derivedCustomFields}
          applicableJurisdictions={applicableJurisdictions}
          applicableCountries={applicableCountries}
          workflowCompletion={workflowCompletion}
          hasMailingAddress={hasMailingAddress}
          hasAlternateAddress={hasAlternateAddress}
          setHasMailingAddress={setHasMailingAddress}
          setHasAlternateAddress={setHasAlternateAddress}
        />
      )}
    </>
  );
};

export default PersonalInformation;
